
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { API } from '../../utils/api';
import { useAuthContext } from '../../context/AuthContext';

export function MobPayHistory({ myRequests }) {
  const { headers } = useAuthContext();
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  // Fetch categories from ALL bound parents to populate filter
  useEffect(() => {
    const fetchCats = async () => {
      try {
        // We need to get categories from all parents. 
        // For simplicity, we'll just extract unique category names from the history itself
        // since MobPay might be bound to multiple parents with different category sets.
        // Alternatively, we could fetch from all parents, but that's more complex.
        // Let's stick to extracting from history for the filter.
        const uniqueCats = [...new Set(myRequests.map(r => r.category_name).filter(Boolean))];
        setCategories(uniqueCats);
      } catch (e) {}
    };
    fetchCats();
  }, [myRequests]);

  // Apply filters
  const filteredRequests = myRequests.filter(r => {
    let matchMonth = true;
    let matchCat = true;

    if (filterMonth !== 'all') {
      try {
        const d = new Date(r.created_at);
        const m = d.toLocaleString('default', { month: 'long' });
        if (m !== filterMonth) matchMonth = false;
      } catch {}
    }

    if (filterCategory !== 'all') {
      if (r.category_name !== filterCategory) matchCat = false;
    }

    return matchMonth && matchCat;
  });

  const groupedRequests = filteredRequests.reduce((groups, r) => {
    try {
      const date = new Date(r.created_at).toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(r);
    } catch (e) {
      const date = 'Unknown Date';
      if (!groups[date]) groups[date] = [];
      groups[date].push(r);
    }
    return groups;
  }, {});

  const formatTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Get unique months for filter
  const months = [...new Set(myRequests.map(r => {
    try { return new Date(r.created_at).toLocaleString('default', { month: 'long' }); } catch { return null; }
  }).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="mobpay-history">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Transaction History</h2>
        <div className="flex gap-2">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {Object.keys(groupedRequests).length === 0 && (
        <div className="text-sm text-muted-foreground">No history found.</div>
      )}

      {Object.entries(groupedRequests).map(([date, items]) => (
        <div key={date} className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground sticky top-0 bg-green-50 py-2 z-10">{date}</h3>
          <div className="space-y-3">
            {items.map((r) => (
              <Card key={r.id} data-testid={`mobpay-history-${r.id}`}>
                <CardContent className="flex items-center gap-3 p-4">
                  <div className="text-sm flex-1">
                    <div className="font-medium">{r.amount ? `${r.amount} ${r.currency}` : (r.source==='STATIC'?'Static payment':'External token')}</div>
                    <div className="text-xs text-muted-foreground">{formatTime(r.created_at)}</div>
                    {r.category_name && <Badge variant="outline" className="mt-1 text-[10px]">{r.category_name}</Badge>}
                  </div>
                  <span className="ml-auto text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border" data-testid={`mobpay-status-${r.id}`}>{r.status}</span>
                  {r.status==='PAID_MANUAL' && (<Badge data-testid={`mobpay-manual-badge-${r.id}`} variant="secondary">Manual</Badge>)}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
